import React from "react";
import {
  Box,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Grid,
  IconButton,
  Button,
  Stack,
  Autocomplete,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { buildSignaturesTemplate, getDefaultRoleLabels, getNextSignerKeyAndEmail } from "../utils/signatureUtils";


// Add no-select style
const noSelectStyle = {
  WebkitTouchCallout: "none",
  WebkitUserSelect: "none",
  KhtmlUserSelect: "none",
  MozUserSelect: "none",
  msUserSelect: "none",
  userSelect: "none",
};

const defaultRoleLabels = getDefaultRoleLabels();

const SignerRoleSelector = ({ signatures, onRolesChange }) => {
  const roles = [
    { key: "client.buy", label: "Buyer" },
    { key: "client.sell", label: "Seller" },
    { key: "team.buy", label: "Buyer Agent" },
    { key: "team.sell", label: "Seller Agent" },
    { key: "other.managingBroker", label: "Managing Broker" },
    { key: "other.propertyManager", label: "Property Manager" },
  ];

  const template = buildSignaturesTemplate();

  const handleChange = (roleKey) => {
    if (signatures[roleKey]) {
      const { [roleKey]: _, ...rest } = signatures;
      onRolesChange(rest);
    } else {
      onRolesChange({ ...signatures, [roleKey]: template[roleKey] });
    }
  };

  const updateRole = (roleKey, updates) => {
    const current = signatures[roleKey];
    if (!current) return;
    onRolesChange({
      ...signatures,
      [roleKey]: { ...current, ...updates },
    });
  };

  const updateRoleNumber = (roleKey, field, value) => {
    const num = value === "" ? undefined : Number(value);
    if (num !== undefined && Number.isNaN(num)) return;
    updateRole(roleKey, { [field]: num });
  };

  const addSigner = (roleKey) => {
    const current = signatures[roleKey];
    if (!current) return;
    const sigs = Array.isArray(current.signatures) ? current.signatures : [];
    const existingKeys = sigs.map((s) => s?.signerKey).filter(Boolean);
    const { signerKey, email } = getNextSignerKeyAndEmail(roleKey, existingKeys);
    updateRole(roleKey, {
      signatures: [...sigs, { signerKey, email }],
    });
  };

  const removeSigner = (roleKey, signerIndex) => {
    const current = signatures[roleKey];
    if (!current) return;
    const sigs = Array.isArray(current.signatures) ? current.signatures : [];
    const next = sigs.filter((_, i) => i !== signerIndex);
    updateRole(roleKey, { signatures: next });
  };

  const updateSigner = (roleKey, signerIndex, field, value) => {
    const current = signatures[roleKey];
    if (!current) return;
    const sigs = [...(Array.isArray(current.signatures) ? current.signatures : [])];
    if (!sigs[signerIndex]) return;
    sigs[signerIndex] = { ...sigs[signerIndex], [field]: value };
    updateRole(roleKey, { signatures: sigs });
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title="Signer Roles"
        subheader="Select the roles that will be signing this form. Expand a role to edit min/max/default, integration roles, and signers."
        sx={noSelectStyle}
      />
      <Divider />
      <CardContent sx={noSelectStyle}>
        <FormGroup row sx={{ mb: 2 }}>
          {roles.map((role) => {
            const effectiveLabel = signatures[role.key]?.label ?? defaultRoleLabels[role.key] ?? role.key;
            return (
              <FormControlLabel
                key={role.key}
                control={
                  <Checkbox
                    checked={!!signatures[role.key]}
                    onChange={() => handleChange(role.key)}
                    sx={{ "&.Mui-checked": { color: "#00a896" } }}
                  />
                }
                label={effectiveLabel}
                sx={{ mr: 4 }}
              />
            );
          })}
        </FormGroup>

        {roles.filter((r) => signatures[r.key]).map((role) => {
          const roleKey = role.key;
          const roleConfig = signatures[roleKey] || {};
          const t = template[roleKey] || {};
          const roleLabel = roleConfig.label ?? defaultRoleLabels[roleKey] ?? roleKey;
          const min = roleConfig.min ?? t.min;
          const max = roleConfig.max ?? t.max;
          const defaultVal = roleConfig.default ?? t.default;
          const additionalSignaturePage = roleConfig.additionalSignaturePage ?? t.additionalSignaturePage;
          const integrationRole = roleConfig.integrationRole ?? t.integrationRole ?? "";
          const meviDealRole = roleConfig.meviDealRole ?? t.meviDealRole ?? roleKey;
          const sigs = Array.isArray(roleConfig.signatures) ? roleConfig.signatures : [];

          return (
            <Accordion key={roleKey} elevation={0} sx={{ "&:before": { display: "none" }, border: "1px solid", borderColor: "divider", borderRadius: 1, mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">
                  {roleLabel} ({roleKey}) — {sigs.length} signer{sigs.length !== 1 ? "s" : ""}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Role label"
                        value={roleConfig.label ?? ""}
                        onChange={(e) => updateRole(roleKey, { label: e.target.value || undefined })}
                        placeholder={defaultRoleLabels[roleKey] ?? roleKey}
                        helperText="Display name (e.g. in Add Signature). Defaults to template label."
                      />
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <TextField
                        size="small"
                        fullWidth
                        type="number"
                        label="Min"
                        value={min ?? ""}
                        onChange={(e) => updateRoleNumber(roleKey, "min", e.target.value)}
                        inputProps={{ min: 0, step: 1 }}
                        helperText="Min signers per role"
                      />
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <TextField
                        size="small"
                        fullWidth
                        type="number"
                        label="Max"
                        value={max ?? ""}
                        onChange={(e) => updateRoleNumber(roleKey, "max", e.target.value)}
                        inputProps={{ min: 0, step: 1 }}
                        helperText="Max signers per role"
                      />
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <TextField
                        size="small"
                        fullWidth
                        type="number"
                        label="Default"
                        value={defaultVal ?? ""}
                        onChange={(e) => updateRoleNumber(roleKey, "default", e.target.value)}
                        inputProps={{ min: 0, step: 1 }}
                        helperText="Default signer count"
                      />
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <TextField
                        size="small"
                        fullWidth
                        type="number"
                        label="Additional signature page"
                        value={additionalSignaturePage ?? ""}
                        onChange={(e) => updateRoleNumber(roleKey, "additionalSignaturePage", e.target.value)}
                        inputProps={{ min: 0, step: 1 }}
                        helperText="Overflow signature page"
                      />
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Integration role"
                        value={integrationRole}
                        onChange={(e) => updateRole(roleKey, { integrationRole: e.target.value })}
                        helperText="Integration system role"
                      />
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Mevi deal role"
                        value={meviDealRole}
                        onChange={(e) => updateRole(roleKey, { meviDealRole: e.target.value })}
                        helperText="Deal system role"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Autocomplete
                        multiple
                        size="small"
                        options={roles.map((r) => r.key).filter((k) => k !== roleKey && !!signatures[k])}
                        value={Array.isArray(roleConfig.altSigners) ? roleConfig.altSigners : []}
                        onChange={(_, v) =>
                          updateRole(roleKey, {
                            altSigners: (v || []).map((x) => String(x || "").trim()).filter(Boolean),
                          })
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Alt signer roles"
                            helperText="Other deal roles treated as either/or with this role at runtime (published as altSigners on signaturesJSON)."
                          />
                        )}
                      />
                    </Grid>
                  </Grid>

                  <Typography variant="subtitle2">Signers</Typography>
                  {sigs.map((sig, idx) => (
                    <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <TextField
                        size="small"
                        label="Signer key"
                        value={sig.signerKey ?? ""}
                        onChange={(e) => updateSigner(roleKey, idx, "signerKey", e.target.value)}
                        sx={{ minWidth: 120 }}
                      />
                      <TextField
                        size="small"
                        label="Email"
                        value={sig.email ?? ""}
                        onChange={(e) => updateSigner(roleKey, idx, "email", e.target.value)}
                        sx={{ minWidth: 180 }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => removeSigner(roleKey, idx)}
                        aria-label={`Remove ${sig.signerKey || "signer"}`}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                  <Button size="small" startIcon={<AddIcon />} onClick={() => addSigner(roleKey)}>
                    Add signer
                  </Button>
                </Stack>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default SignerRoleSelector;
